import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from "@ag-grid-community/react";
import { MenuModule } from '@ag-grid-enterprise/menu';
import {ColDef, CsvExportParams, ExcelExportParams, GetContextMenuItemsParams, GridOptions, MenuItemDef, SortChangedEvent} from "@ag-grid-community/all-modules";
import defaultGridOptions from 'common/agGrid/defaultGridOptions';
import Permissions from "common/security/permissions";
import usePermission from 'common/security/usePermission';
import { useDispatch, useSelector } from 'react-redux';
import IAppStore from 'app/IAppStore';
import ConfirmationModal, { useConfirmationModal } from 'common/ui/ConfirmationModal';
import Contract, { ContractType } from 'domain/contract';
import notificationService from 'common/notificationService';
import useScreenSize from 'common/ui/useScreenSize';
import { Producer, getProducerDisplayName } from 'features/settings/producers/producer';
import columnBuilder from 'common/agGrid/columnBuilder';
import { RowMenuButton, handleRowMenuClick } from 'common/agGrid/RowMenuButton';
import { formatCustomerWillSell, formatDate, formatDecimalPlace } from 'common/formatters';
import { getPaymentOptionDisplayName } from 'domain/paymentOption';
import { useReactToPrint } from 'react-to-print';
import TableApi from 'common/ui/tableApi';
import EditContractModal from './EditContractModal';
import { ContractColumn, ContractColumnConfigurations, ContractListColumnsFilter, getColumnBuilder } from './ContractListColumns';
import ValidationError from 'common/validationError';
import * as ContractActions from "./contracts.actions";
import { ColumnCustomization } from 'common/agGrid/columnCustomization';
import { allowAddOfferOrContract, allowEdit, allowSetBasis, allowSetFutures, isContractTypeAllowed } from './helpers';
import { calculateRequiredFuturesPrice } from 'features/offers/priceCalculator';
import AddOfferModel from 'features/offers/addOfferModel';
import { getModelForDelayedPrice, getModelForSetBasis, getModelForSetFutures } from 'features/offers/helpers';
import deliveryPeriodService from 'features/deliveryPeriods/deliveryPeriod.service';
import DeliveryPeriod, { getDeliveryPeriodDisplayName } from 'domain/deliveryPeriod';
import contractService from './contractService';
import SyncContractToErpRetryModal from './SyncContractToErpRetryModal';
import AddOfferModal from 'features/offers/AddOfferModal';
import unitHelper from "domain/unitHelper";
import './ContractTable.scss';
import useEventListener from 'common/ui/useEventListener';
import contractTypeHelper from 'domain/contractTypeHelper';
import useTranslators from 'common/translation/useTranslators';
import { localizeOfferContract as localize } from "features/localization/localizeService";
import useUnitConversionRates from 'features/settings/commodities/useUnitConversionRates';
import useOfferings from 'features/settings/offerings/useOfferings';
import useContractColumns from './useContractColumns';
import { usePaymentOptions } from 'features/merchants/usePaymentOptions';
import useFeatureFlag from 'features/featureFlags/useFeatureFlag';

export interface ContractTableProps {
    contractTableType: ContractTableType;
    contracts: Contract[];
    columnIds: string[];
    search?: string;
    onCustomerSelected?(customer: Producer): void;
    setTableApi?(api: TableApi): void;
    gridOptions?: GridOptions;
    showQuantityUnits?: boolean;
}

const defaultColDef: any = {
    sortable: true,
    minWidth: 60
};

type SortDirection = "asc" | "desc";

type SelectedSort = {
    type: ContractTableType;
    colId: string;
    sortOrder: SortDirection;
};

export enum ContractTableType {
    Cash = 1,
    Basis = 2,
    Hta = 3,
    DelayedPrice = 4,
    WithOptions = 5
}

const gridModules = [MenuModule];

const ContractTable: React.FC<ContractTableProps> = props => {
    
    const [gridColumns, setGridColumns] = useState<ColDef[]>(null);
    const [showSetBasisOrSetFuturesModal, setShowSetBasisOrSetFuturesModal] = useState(false);
    const [showPricingDelayedPriceContractModal, setShowPricingDelayedPriceContractModal] = useState(false);
    const [selectedSortForStatus, setSelectedSortForStatus] = useState<SelectedSort>(null);
    const [addOfferModel, setAddOfferModel] = useState<AddOfferModel>(null);
    const [isEditContractModalVisible, showEditContractModal] = useState(false);
    const [editingContract, setEditingContract] = useState<Contract>(null);
    const [showRetryErpSyncModal, setShowRetryErpSyncModal] = useState(false);
    const [retryErpSyncForContract, setRetryErpSyncForContract] = useState<Contract>(null);

    const userConfiguration = useSelector((m: IAppStore) => m.userState.userConfiguration);
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const isCustomerAndNotAllowedContractPricing = isCustomer && !useFeatureFlag("AllowContractPricingForCustomers");
    const customerAccounts = useSelector((m: IAppStore) => m.userState.customerAccounts);
    const activeMerchant = useSelector((m: IAppStore) => m.userState.activeMerchant);

    const myCommodities = useSelector((m: IAppStore) => m.myCommodities);
    const offerings = useOfferings();
    const conversionRates = useUnitConversionRates();
    const paymentOptionsDetails = usePaymentOptions();

    const canEditContracts = usePermission(Permissions.EditContracts);
    const canPushBasis = usePermission(Permissions.PushBasis);
    const canSupervisorEdit = usePermission(Permissions.SupervisorEditContract);
    
    const confirmModal = useConfirmationModal();
    const dispatch = useDispatch();
    
    const componentRef = useRef();
    const gridRef = useRef<AgGridReact>();
    
    const screenSize = useScreenSize();
    const useMobileColumns = screenSize.isAtMost("sm");

    const contractColumns = useContractColumns(useMobileColumns);

    const translators = useTranslators();

    const filter: ContractListColumnsFilter = {
        ...new ContractListColumnsFilter(),
        isCustomerEditAllowed : usePermission(Permissions.ManageProducer),
        onCustomerSelected: props.onCustomerSelected, 
        isCustomer: isCustomer, 
        isMobile: useMobileColumns, 
        conversionRates: conversionRates, 
        translators: translators, 
        onContractFlagUpdated: onFlagUpdated, 
        onErpIdSelected: onErpIdSelected, 
        showQuantityUnits: props.showQuantityUnits, 
        merchant: activeMerchant
    }

    const contractColumnBuilder = getColumnBuilder(filter);
    
    useEventListener('resize', handleResize);

    useEffect(() => {
        if (props.columnIds == null) return;
        
        setGridColumns(getColumnDefinitions());
    }, [props.contractTableType, props.columnIds]);

    useEffect(() => {
        if (props.setTableApi != null) {
            props.setTableApi({
                exportAsCsv: exportAsCsv,
                exportAsExcel: exportAsExcel,
                print: printGrid
            })
        }
    }, [props.contractTableType]);
    

    const handleGridReady = useCallback(params => {
        if(contractColumns != null)
        {
            setInitialSortForStatus(props.contractTableType);
        }     
        params.api.sizeColumnsToFit();
        params.columnApi.autoSizeColumn("quantity");
        }, []);

    function handleResize() {
        gridRef.current.api.sizeColumnsToFit()
    }

    const printWithReact = useReactToPrint({
        onAfterPrint: () => setNormalMode(),
        content: () => componentRef.current,
    });
    
    function printGrid() {
        setPrintMode();
        setTimeout(function () {
            printWithReact();
        }, 1000);
    }

    function setPrintMode() {
        gridRef.current.api.setDomLayout('print');
    }

    function setNormalMode() {
        gridRef.current.api?.setDomLayout('autoHeight');
    }

    function exportAsCsv() {
        gridRef.current.api.exportDataAsCsv(setupCsvParams())
    }

    function exportAsExcel() {
        gridRef.current.api.exportDataAsExcel(setupExcelParams())
    }

    function exportCellCallback(params: any) {
        const data = params.node.data;
        const commodity = data?.commodity;
        const showLocalConversions = commodity?.baseUnit.id !== commodity?.localUnit.id;
        switch (params.column.colId) {
            case ContractColumn.deliveryEnd:
            case ContractColumn.deliveryStart:
                return formatDate(params.value);
            case ContractColumn.customer:
                return getProducerDisplayName(data?.producer);
            case ContractColumn.customerWillSell:
                return formatCustomerWillSell(params.value);
            case ContractColumn.lastUpdatedBy:
                return data?.lastUpdatedBy.name;
            case ContractColumn.createdBy:
                return data?.createdBy.name;
            case ContractColumn.quantity:
                return data?.quantityContracted;
            case ContractColumn.cashPrice:
            case ContractColumn.basis:
                return params.value ? params.value.toLocaleString() : '';
            case ContractColumn.fillPrice:
                return params.value == null ? '' : formatDecimalPlace(params.value);
            case ContractColumn.futuresPrice:
                return data.offer == null ? null : calculateRequiredFuturesPrice(data.offer, commodity?.isCashOnly);
            case ContractColumn.myBasis:
                return contractTypeHelper.isHta(data.contractType) ? '' : formatDecimalPlace(params.value);
            case ContractColumn.lastUpdatedDate:
            case ContractColumn.fillTime:
                    return formatDate(params.value, "MM/DD/YY LTS");
            case ContractColumn.flag:
                return params.value ? "Yes": "No";
            case ContractColumn.paymentOption:
                return getPaymentOptionDisplayName(params.value, paymentOptionsDetails);
            case ContractColumn.uom:
                return commodity?.baseUnit.abbreviation;
            case ContractColumn.localQuantity:
                return showLocalConversions ? unitHelper.getLocalQuantity(data?.quantityContracted, commodity, conversionRates) : '';
            case ContractColumn.localUom:
                return showLocalConversions ? commodity?.localUnit.abbreviation : '';
            default:
                return params.value;
        }
    }

    function setupExcelParams(): ExcelExportParams {
        return {
            skipColumnHeaders: false,
            allColumns: false,
            onlySelected: false,
            columnKeys: generateColumnsForExcel(),
            fileName: `${getContractTableTypeName(props.contractTableType)}.xlsx`,
            processHeaderCallback: exportHeaderCallback,
            processCellCallback: exportCellCallback
        };
    }

    function setupCsvParams(): CsvExportParams {
        return {
            skipColumnHeaders: false,
            allColumns: false,
            onlySelected: false,
            columnKeys: generateColumnsForExcel(),
            fileName: `${getContractTableTypeName(props.contractTableType)}.csv`,
            columnSeparator: ',',
            processHeaderCallback: exportHeaderCallback,
            processCellCallback: exportCellCallback
        };
    }

    function generateColumnsForExcel(): string[] {
        const keys = [...props.columnIds];
        const quantityIndex: number = keys.indexOf('quantity');
        quantityIndex != -1 && keys.splice(quantityIndex + 1, 0, ContractColumn.uom, ContractColumn.localQuantity, ContractColumn.localUom);
        return keys;
    }

    function exportHeaderCallback(params: any) {
        switch (params.column.colId) {
            case ContractColumn.quantity:
                return 'Base Qty';
            default:
                return params.column.colDef.headerName;
        }
    }

    async function onFlagUpdated(contract: Contract) {
        try {
            await ContractActions.updateContractFlag(dispatch, contract);
        }
        catch (error) {
            notificationService.error(((error as ValidationError).errors ?? ["An unknown error occurred"])[0]);
        }
    }

    async function onErpIdSelected(contract: Contract) {
        setShowRetryErpSyncModal(true);
        setRetryErpSyncForContract(contract);
    }

    function handleErpIdSelectionCancel() {
        setShowRetryErpSyncModal(false);
        setRetryErpSyncForContract(null);
    }

    async function retryErpSync(contract: Contract) {
        await contractService.retryContractSyncingToErp(contract);
        setShowRetryErpSyncModal(false);
        setRetryErpSyncForContract(null);
    }
    
    function getColumnDefinitions(): ColDef[] {
        const configured = contractColumnBuilder.listColumns([...props.columnIds, ContractColumn.uom, ContractColumn.localQuantity, ContractColumn.localUom]);

        const actionMenu = columnBuilder.for("", "", { sortable: false})
            .withCellClass("menu-cell")
            .withWidth(30)
            .withCellRenderer<Contract>(params => params.node.group || !canManageAccounts(params.node.data) ? null : <RowMenuButton />)
            .onCellClick(event => canManageAccounts(event.node.data) ? handleRowMenuClick(event) : null)
            .build();

        return [actionMenu, ...configured || []];
    }

    function canManageAccounts(contract: Contract) {
        if (!isCustomer || contract.customerAccount == null) {
            return true;
        }
        else {
            return customerAccounts.some(x => x.accountId === contract.customerAccount?.accountId && x.canManage);
        }
    }

    const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): MenuItemDef[] => {

        if (params.node == null) return []; //User right-clicked on empty space

        const contract = params.node.data as Contract;
        const menuItems: MenuItemDef[] = [];
         
        if (!isCustomer && !isContractTypeAllowed(contract.contractType, userConfiguration)) {
            return [{
                name: "Cannot modify this contract",
                disabled: true
            }];
        }

        if (!isCustomerAndNotAllowedContractPricing && allowSetFutures(contract, offerings, isCustomer, userConfiguration)) {
            menuItems.push({
                name: "Set Futures",
                action: () => placeFixFutures(contract),
                disabled: contract.disablePricings,
                tooltip: contract.disablePricings ? "Cannot set futures on this contract": null
            });
        }

        if (!isCustomer && allowEdit(contract, userConfiguration)) {
            menuItems.push({
                name: "Edit Contract",
                action: () => onEditingContract(contract, false),
                disabled: contract.isReadOnly,
                tooltip: contract.isReadOnly  ? "Read-only contracts cannot be edited" : null
            });
        }

        if (canSupervisorEdit) {
            menuItems.push({
                name: "Supervisor Edit",
                action: () => onEditingContract(contract, true),
                tooltip: "Read-only contracts can be edited, but changes will not be sent to your ERP"
            });
        }

        if (allowAddOfferOrContract(contract, offerings, isCustomer, userConfiguration)) {
            menuItems.push({
                name: "Add Offer/Contract",
                disabled: contract.disablePricings,
                tooltip: contract.disablePricings ? "Cannot add offer/contract on this contract": null,
                action: () => priceDelayedPrice(contract)
            });
        }

        if (!isCustomerAndNotAllowedContractPricing && allowSetBasis(contract, offerings, isCustomer, userConfiguration)) {
            menuItems.push({
                name: "Set Basis",
                action: () => placeSetBasis(contract),
                disabled: contract.disablePricings,
                tooltip: contract.disablePricings ? "Cannot set basis on this contract": null
            });
        }

        if ([ContractType.Hta, ContractType.HtaMinPrice, ContractType.Basis].includes(contract.contractType) && canEditContracts && contract.remainingQuantity > 0) {
            menuItems.push({
                name: "Close Contract",
                action: () => closeContract(contract)
            });
        }

        if (canArchiveContract(contract) && !contract.isArchived) {
            menuItems.push({
                name: "Archive Contract",
                action: () => archiveContract(contract)
            });
        }

        if (canArchiveContract(contract) && contract.isArchived) {
            menuItems.push({
                name: "Un-Archive Contract",
                action: () => unarchiveContract(contract)
            });
        }
        return menuItems;
    }, [canEditContracts, canSupervisorEdit, offerings, isCustomer, userConfiguration]);
    
    function canArchiveContract(contract: Contract): boolean {
        return [ContractType.Cash, ContractType.CashMinPrice].includes(contract.contractType) && (isCustomer || canEditContracts);
    }

    const onSortChanged = useCallback(function(ev: SortChangedEvent)
    {
        const columnState = ev.columnApi.getColumnState();
        const sortedColumn = columnState.find(m => m.sort != null);
        if(sortedColumn && selectedSortForStatus != null  && selectedSortForStatus.type === props.contractTableType && (selectedSortForStatus.colId !== sortedColumn.colId || selectedSortForStatus.sortOrder !== sortedColumn.sort))
        {
            const config = {...contractColumns};
            const selectedSort: SelectedSort = {
                colId: sortedColumn.colId, 
                sortOrder: sortedColumn.sort,
                type: props.contractTableType
            };
            setSortOrderForType(config, selectedSort);
    
            //we don't allow customization of mobile columns
            if (useMobileColumns) return;
            
            ContractActions.saveContractColumnConfigurations(dispatch, config);
            setSelectedSortForStatus(selectedSort);
        }
        ev.columnApi.autoSizeColumn("quantity");
    }, [props.contractTableType])

    function setInitialSortForStatus(contractTableType: ContractTableType) {
        const initialSort: SelectedSort = {
            colId: "id",
            sortOrder: "desc",
            type: contractTableType
        };

        let contractConfig: ColumnCustomization = null;
        switch (props.contractTableType) {
            case ContractTableType.Basis:
                contractConfig = contractColumns.basis;
                break;
            case ContractTableType.Hta:
                contractConfig = contractColumns.hta;
                break;
            case ContractTableType.Cash:
                contractConfig = contractColumns.cash;
                break;
            case ContractTableType.DelayedPrice:
                contractConfig = contractColumns.delayedPrice;
                break;
            case ContractTableType.WithOptions:
                contractConfig = contractColumns.withOptions;
                break;
            default:
                throw new Error("Unrecognized offer status");
        };

        if (contractConfig.preferredSortColumnId && contractConfig.preferredSortOrder
            && contractConfig.visibleColumns.includes(contractConfig.preferredSortColumnId)) {
            initialSort.colId = contractConfig.preferredSortColumnId;
            initialSort.sortOrder = contractConfig.preferredSortOrder as SortDirection
        };

        setSelectedSortForStatus(initialSort);
        gridRef.current.columnApi.applyColumnState({
            state: [{
                colId: initialSort.colId,
                sort: initialSort.sortOrder
            }]
        });
    }

    async function placeFixFutures(contract: Contract) {
        const deliveryPeriod = await getDeliveryPeriod(contract.deliveryPeriod?.id);
        if (deliveryPeriod == null) {
            confirmModal.show(
                "Set Futures Not Allowed",
                <>This contract cannot be priced, as it doesn't have an active delivery period. Please inform your head merchandiser.</>,
                undefined,
                undefined,
                true
            );
            return;
        }
        setAddOfferModel(getModelForSetFutures(contract));
        setShowSetBasisOrSetFuturesModal(true);
    }

    async function placeSetBasis(contract: Contract) {
        if (!canPushBasis && contract.offer.currentBasis == null) {
            confirmModal.show(
                "Set Basis Not Allowed",
                <>There is no basis value for delivery period <strong>{getDeliveryPeriodDisplayName(contract.deliveryPeriod)}</strong>.
                    Please consult with your head merchandiser.</>,
                undefined,
                undefined,
                true
            );
            return;
        }
        
        const deliveryPeriod = await getDeliveryPeriod(contract.deliveryPeriod?.id);
        if (deliveryPeriod == null) {
            confirmModal.show(
                "Set Basis Not Allowed",
                <>This contract cannot be priced, as it doesn't have an active delivery period. Please inform your head merchandiser.</>,
                undefined,
                undefined,
                true
            );
            return;
        }
        
        setAddOfferModel(getModelForSetBasis(contract));
        setShowSetBasisOrSetFuturesModal(true);
    }
    
    async function getDeliveryPeriod(id: string): Promise<DeliveryPeriod> {
        return await deliveryPeriodService.getDeliveryPeriod(id);
    }

    function onEditingContract(contract: Contract, isSupervisorEditing: boolean) {
        setEditingContract({ ...contract, commodity: contract.commodity, isSupervisorEditing: isSupervisorEditing });
        showEditContractModal(true);
    }

    function closeContract(contract: Contract) {
        confirmModal.show(
            "Close Contract?", 
            <>Closing out the contract <strong>#{contract.displayId}</strong> will zero out the remaining quantity.</>, 
            async () => {
                ContractActions.closeContract(contract)(dispatch);
            }
        );
    }

    function archiveContract(contract: Contract) {
        confirmModal.show(
            "Archive Contract?", 
            <>Archiving the contract will hide the contract from the Open display.</>, 
            async () => {
                ContractActions.archiveContract(contract)(dispatch);
            }
        );
    }

    function unarchiveContract(contract: Contract) {
        confirmModal.show(
            "Un-Archive Contract?", 
            <>Un-Archiving the contract will move the contract back to the Open display.</>, 
            async () => {
                ContractActions.unarchiveContract(contract)(dispatch);
            }
        );
    }

    function priceDelayedPrice(contract: Contract) {
        setAddOfferModel(getModelForDelayedPrice(contract));
        setShowPricingDelayedPriceContractModal(true);
    }
    
    function isLoaded(): boolean {
        return myCommodities != null;
    }

    function onClose() {
        setShowSetBasisOrSetFuturesModal(false); 
        setShowPricingDelayedPriceContractModal(false);
        setAddOfferModel(null);
    }
    
    if (!isLoaded()) return <></>;

    return <div ref={componentRef} className="contract-table w-100">
        <div className="ag-theme-alpine full-width">
            <AgGridReact ref={gridRef}
                {...defaultGridOptions}
                {...props.gridOptions}
                columnDefs={gridColumns}
                defaultColDef={defaultColDef}
                modules={gridModules}
                suppressMultiSort
                rowData={props.contracts}
                quickFilterText={props.search}
                paginationPageSize={25}
                pagination
                domLayout="autoHeight"
                getRowId={getRowId} 
                getContextMenuItems={getContextMenuItems}
                popupParent={document.querySelector("body")}
                onRowDataUpdated={handleRowDataUpdated}
                onGridColumnsChanged={handleGridColumnsChanged}
                onGridReady={handleGridReady}
                onSortChanged={onSortChanged}
                />
        </div>
        
        {(showSetBasisOrSetFuturesModal || showPricingDelayedPriceContractModal) &&
            <AddOfferModal
                model={addOfferModel}
                isPricingDelayedPriceContractModal={showPricingDelayedPriceContractModal}
                onClose={onClose} />
        }
        {isEditContractModalVisible && <EditContractModal 
            contract={editingContract} 
            onClose={() => showEditContractModal(false)} />
        }
        <ConfirmationModal modalState={confirmModal.state} />
        {showRetryErpSyncModal && 
            <SyncContractToErpRetryModal
                contract={retryErpSyncForContract}
                showModal={showRetryErpSyncModal}
                handleCancel={handleErpIdSelectionCancel}
                retryErpSync={(contract: Contract) => retryErpSync(contract)}
                />
        }
    </div>;
}

const getRowId = (params: any) => params.data.id;
const handleRowDataUpdated = (params: any) => params.columnApi.autoSizeColumn("quantity");
const handleGridColumnsChanged = (params:any) => {
    params.api.sizeColumnsToFit();
    params.columnApi.autoSizeColumn("quantity");
};

export default ContractTable;

function setSortOrderForType(config: ContractColumnConfigurations, sort: SelectedSort) {
    
    const newVals = {
        preferredSortColumnId: sort.colId,
        preferredSortOrder: sort.sortOrder
    };
    switch (sort.type) {
        case ContractTableType.Basis:
            config.basis = {...config.basis, ...newVals};
            break;

        case ContractTableType.Cash:
            config.cash = {...config.cash, ...newVals};
            break;

        case ContractTableType.Hta:
            config.hta = {...config.hta, ...newVals};
            break;

        case ContractTableType.DelayedPrice:
            config.delayedPrice = {...config.delayedPrice, ...newVals};
            break;

        case ContractTableType.WithOptions:
            config.withOptions = {...config.withOptions, ...newVals};
            break;

        default:
            throw new Error("Unrecognized contract type");
    }
}

export function getContractTableTypeName(contractType: ContractTableType): string {
    switch (contractType) {
        case ContractTableType.Cash:
            return localize("Domain_Value_Cash");
        case ContractTableType.Hta:
            return localize("Domain_Value_Hta");
        case ContractTableType.Basis:
            return localize("Domain_Value_Basis");
        case ContractTableType.DelayedPrice:
            return localize("Domain_Value_DelayedPrice");
        case ContractTableType.WithOptions:
            return localize("Domain_Value_WithOptions");
        default:
            return localize("ContractTable_Value_Unknown");
    }
}